import React, { PureComponent } from "react";
import Uri from "jsuri";

import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  Metric,
  StandardTooltip,
  CustomizedXAxisTickTimeDay,
  CustomizedYAxisTick,
  CustomizedYAxisTickRight,
  selectFormat,
  Error,
  formatDateFromData,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
} from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XRayTooltip/XRayTooltip.js";

import { getXrayTooltipPosition } from "helpers/DOM";

const GRADIENT = (type) =>
  ({
    facebook: ["#CAFFF0", "#16DE85", "#16DE85"],
    social: ["#FFC7CD", "#FF283F", "#FF283F"],
    youtube: ["#F8DCD5", "#FC6D52", "#F89481"],
    twitter: ["#FFF7DA", "#FCD23A", "#FCD23A"],
    display: ["#DCF5F7", "#16C8DE", "#DCF5F7"],
    search: ["#F6D9EF", "#F3108E", "#F3108E"],
    // social: ["#F8DCD5", "#F89481", "#FC6D52"],
    // sizmekmdx: ["#DCF5F7", "#16C8DE", "#16C8DE"],
    // gdn: ["#F6D9EF", "#F3108E", "#F3108E"],
  })[type] || ["#DCF5F7", "#16C8DE", "#DCF5F7"];

class AdsPlatforms extends PureComponent {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef || !this.chartRef.state) return;

    const containerWidth = this.chartRef.state.containerWidth || 0;
    const containerHeight = this.chartRef.state.containerHeight || 0;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  render() {
    const { data, url } = this.props;
    const { widgetsInsights } = this.state;

    const widget_data = data.graph;
    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    const url_channel = new Uri(url).getQueryParamValue("channel");
    let platform = "";

    if (url_channel) {
      if (url_channel.includes(",")) {
        platform = url_channel.split(",")[0].toLowerCase();
      } else {
        platform = url_channel.toLowerCase();
      }
    }

    let preparedData = formatDateFromData(widget_data);

    return (
      <>
        <div key={"stats"} className="row">
          {data.data[0].impressions && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">Impressions</h6>
              <Metric {...data.data[0].impressions} size={"sm"} />
            </div>
          )}

          {data.data[0].clicks && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">Clicks</h6>
              <Metric {...data.data[0].clicks} size={"sm"} />
            </div>
          )}

          {data.data[0].cpm && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">CPM</h6>
              <Metric {...data.data[0].cpm} size={"sm"} />
            </div>
          )}

          {data.data[0].cpc && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">CPC</h6>
              <Metric {...data.data[0].cpc} size={"sm"} />
            </div>
          )}

          {data.data[0].total_spend && (
            <div className="col-6 col-md-3 mb-3">
              <h6 className="widget__metric-label">Total Spend</h6>
              <Metric {...data.data[0].total_spend} size={"sm"} />
            </div>
          )}
        </div>

        <div className="row" ref={this.divPosition}>
          <ResponsiveContainer
            className="legends-non-interactive"
            height={250}
            width="100%"
            key={"graph"}
            ref={(ref) => (this.chartRef = ref)}
          >
            <ComposedChart
              data={preparedData}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(this.node, !this.state.tooltip.show)
              }
              isAnimationActive={false}
              onClick={(d) => {
                const extra = {
                  impressions: {
                    label: "Impressions",
                    color: GRADIENT(platform)[0],
                  },
                  clicks: { label: "Clicks", color: GRADIENT(platform)[2] },
                  cpm: { label: "CPM", color: "#F3108E" },
                  cpc: { label: "CPC", color: "#FCD23A" },
                };

                if (d !== null) {
                  d.extra = extra;
                  const indicator = "adsPlatforms";
                  d.indicator = indicator;

                  // Ensure activePayload is properly formatted to avoid errors in XRayLineChartTooltip
                  if (!d.activePayload) {
                    d.activePayload = {
                      0: {
                        name: "clicks",
                        value: d.payload ? d.payload.clicks : 0,
                        color: GRADIENT(platform)[2],
                      },
                      1: {
                        name: "impressions",
                        value: d.payload ? d.payload.impressions : 0,
                        color: GRADIENT(platform)[0],
                      },
                    };
                  }

                  this.setState({
                    tooltip: { ...d, show: true },
                  });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              <defs>
                <linearGradient
                  id={`GRADIENT-${platform}`}
                  x1="0"
                  y1="1"
                  x2="0"
                  y2="0"
                >
                  <stop
                    offset="5%"
                    stopColor={GRADIENT(platform)[0]}
                    stopOpacity={1}
                  />
                  <stop
                    offset="95%"
                    stopColor={GRADIENT(platform)[1]}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Area
                yAxisId="left"
                scale="auto"
                dataKey="impressions"
                type="monotone"
                fillOpacity={0.2}
                fill={`url(#GRADIENT-${platform})`}
                stroke={GRADIENT(platform)[0]}
                isAnimationActive={false}
              >
                {insightsData && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={180}
                        data={preparedData}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Area>

              <YAxis
                dataKey="impressions"
                interval={"preserveEnd"}
                yAxisId="left"
                orientation="left"
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />

              <YAxis
                dataKey="clicks"
                interval={"preserveEnd"}
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                mirror={true}
                stroke={GRADIENT(platform)[2]}
                tick={<CustomizedYAxisTickRight />}
              />

              <XAxis
                dataKey="date"
                interval={"preserveStartEnd"}
                scale="point"
                axisLine={false}
                tickLine={false}
                tick={<CustomizedXAxisTickTimeDay />}
                stroke={GRADIENT(platform)[2]}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="square"
                iconSize={15}
                height={35}
                wrapperStyle={{ bottom: -10 }}
              />
              <Line
                yAxisId="right"
                scale="auto"
                dataKey="clicks"
                type="monotone"
                stroke={GRADIENT(platform)[2]}
                strokeWidth="2"
                dot={false}
                name="Clicks"
              />
              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);

                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          return (
                            <div className="standard-tooltip">
                              {tooltip_data && tooltip_data.date && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {tooltip_data.date}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data && tooltip_data.impressions && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[1] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[1].stroke,
                                          }}
                                        ></span>
                                      )}
                                      Impressions
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.impressions
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data.clicks && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      {payload[0] && (
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: payload[0].stroke,
                                            opacity: 0.8,
                                          }}
                                        ></span>
                                      )}
                                      Clicks
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.clicks
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data.cpm && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      CPM
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "currency",
                                        tooltip_data.cpm
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data && tooltip_data.cpc && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      CPC
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "currency",
                                        tooltip_data.cpc
                                      )}
                                    </span>
                                  </div>
                                )}

                                {insightsData && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {this.state.tooltip.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: "130px",
                left:
                  this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                  this.state.width
                    ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                    : this.state.tooltip.activeCoordinate.x -
                      getXrayTooltipPosition(
                        this.divPosition.current,
                        this.state.tooltip.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltip.show ? 1.0 : 0.0,
                visibility: this.state.tooltip.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltip}
                insights={widgetsInsights}
                close={this.closeTooltip.bind(this)}
              />
            </div>
          ) : null}

          <div className="featured-insight-tooltip" ref={this.node}>
            {insightsData && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
      </>
    );
  }
}

export default AdsPlatforms;
